import React, { Component } from 'react';
import cn from 'classnames';
import { connect } from 'react-redux';
import { Link } from 'react-router';
import PropTypes from 'prop-types';

import { setHeaderFooterVisibility } from 'actions/headerfooter';
import { trackEvent } from 'helpers/analytics';
import { setLastPageWasOrderconfirmation } from 'store/ducks/history/actions';
import { onFetchOrdersByPurchaseId } from 'store/ducks/checkout/actions';
import { firePixelServer } from 'actions/pixelServer';
import CartItem from 'components/cart/CartItem';
import MultiLineAddress from 'components/checkout/melody/address/MultiLineAddress';
import ContactUs from 'components/checkout/melody/typ/ContactUs';
import VoiceOfCustomer from 'components/checkout/melody/typ/VoiceOfCustomer';
import { Loader } from 'components/Loader';
import SiteAwareMetadata from 'components/SiteAwareMetadata';
import { pluralize } from 'helpers';
import { inIframe } from 'helpers/CheckoutUtils';
import { isDesktop } from 'helpers/ClientUtils';
import { toUSD } from 'helpers/NumberFormats';
import { onEvent, supportsPassiveEventListener } from 'helpers/EventHelpers';
import { filter } from 'helpers/lodashReplacement';
import { toFormatted } from 'store/ducks/address/utils';
import css from 'styles/containers/checkout/melody/orderConfirmation.scss';
import {
  heading as cartHeading,
  items as cartItems,
  wrapper as cartWrapper
} from 'styles/containers/cart.scss';

export class OrderConfirmation extends Component {
  componentDidMount() {
    const {
      onFetchOrdersByPurchaseId,
      params: { purchaseId },
      setLastPageWasOrderconfirmation
    } = this.props;

    const opts = supportsPassiveEventListener() ? { passive: true } : false;
    onEvent(window, 'resize', this.onResize, opts, this);
    this.onResize();

    setLastPageWasOrderconfirmation(true);
    trackEvent('TE_PV_ORDERCONFIRMATION');
    onFetchOrdersByPurchaseId(purchaseId);
  }

  componentDidUpdate(prevProps) {
    const { firePixelServer, melodyCheckoutData: { confirmationPixelPayload } } = this.props;
    const { melodyCheckoutData: { confirmationPixelPayload: prevConfirmationPixelPayload } } = prevProps;
    if (confirmationPixelPayload && confirmationPixelPayload !== prevConfirmationPixelPayload) {
      // make order total available to mPulse
      window.orderTotal = confirmationPixelPayload.order.grandTotal;

      // fire pixel server
      firePixelServer('confirmation', confirmationPixelPayload);
    }
  }

  componentWillUnmount() {
    this.props.setHeaderFooterVisibility(true);
  }

  onClickContinueShopping = () => {
    trackEvent('TE_ORDER_CONFIRMATION_CONTINUE_SHOPPING');
  }

  onResize = () => {
    const { setHeaderFooterVisibility } = this.props;
    const isInFrame = typeof window !== 'undefined' && inIframe(window);
    const { marketplace } = this.context;
    const isHideHeaderFooter = isInFrame || !isDesktop(marketplace);
    setHeaderFooterVisibility(!isHideHeaderFooter);
  }

  make2ColOrder(order) {
    const { items } = order;
    const eGiftCards = filter(items, item => item.egcInfo);
    return eGiftCards.length ? this.makeEGiftCard(order) : this.make2ColRetail(order);
  }

  makeOrder(order) {
    const { items } = order;
    const eGiftCards = filter(items, item => item.egcInfo);
    return eGiftCards.length ? this.makeEGiftCard(order) : this.makeRetail(order);
  }

  make2ColRetail(order) {
    const {
      orderId,
      orderDate,
      shipOptionPromise,
      paymentInfo: { paymentType, addCreditCardNumber },
      price: { shipping, subTotal, tax, total },
      items,
      shippingAddress = {}
    } = order;

    const { testId } = this.context;
    const [ firstItem ] = items;
    const { shippingInfo: { earliestArrivalDate, latestArrivalDate } } = firstItem;
    const formattedAddress = toFormatted(shippingAddress);
    const dateReviewed = new Date(orderDate);
    const dateFormatOptions = { month: 'long', day: '2-digit', year: 'numeric' };

    return (
      <>
        <div className={css.twoColumnWrapper} data-test-id={testId('orderDetailsSection')}>
          <div>
            <h2>Order Details</h2>
            <dl className={css.detailRow}>
              <dt>Order Number:</dt>
              <dd>
                <Link
                  className={css.reviewOrderLink}
                  data-test-id={testId('orderReviewLink')}
                  to={`/orders/${orderId}`}>{orderId}</Link>
              </dd>
            </dl>
            <dl className={css.detailRow}>
              <dt>Date Ordered:</dt>
              <dd>{dateReviewed.toLocaleString('en-US', dateFormatOptions)}</dd>
            </dl>
            <dl className={css.detailRow}>
              <dt>Shipping Speed:</dt>
              <dd>{shipOptionPromise}</dd>
            </dl>
            <dl className={css.detailRow}>
              <dt>Estimated Delivery:</dt>
              <dd>{this.makePromise(earliestArrivalDate, latestArrivalDate)}</dd>
            </dl>
            <dl className={css.detailRow}>
              <dt>Payment Method:</dt>
              <dd>{paymentType} - ****{addCreditCardNumber}</dd>
            </dl>
            <dl className={css.detailRow}>
              <dt>Order Total:</dt>
              <dd>{toUSD(total)}</dd>
            </dl>
          </div>
          <div>
            <div className={css.shipAddressTitle}>Shipping To:</div>
            <MultiLineAddress address={formattedAddress} highlightName={false} />
          </div>
        </div>

        <div className={css.itemsPurchasedWrapper} data-test-id={testId('itemsPurchasedSection')}>
          <h2>Items Purchased</h2>
          <div className={cartWrapper}>
            <div className={cn(cartItems, css.cartItems)}>
              <div className={cartHeading}>
                <span>Item</span>
                <span>Quantity</span>
                <span>Price</span>
              </div>
              {items.map(item => {
                const { lineItemId, itemQuantity } = item;
                return <CartItem
                  item={item}
                  key={lineItemId}
                  showFixedQuantity={itemQuantity}
                  hideFavorite={true} />;
              }
              )}
            </div>
          </div>
        </div>

        <div className={css.orderTotalWrapper} data-test-id={testId('orderTotalSection')}>
          <dl className={css.detailRow}>
            <dt>Subtotal:</dt>
            <dd>{toUSD(subTotal)}</dd>
          </dl>
          <dl className={css.detailRow}>
            <dt>Shipping:</dt>
            <dd>{toUSD(shipping)}</dd>
          </dl>
          <dl className={css.detailRow}>
            <dt>Tax:</dt>
            <dd>{toUSD(tax)}</dd>
          </dl>
          <dl className={cn(css.detailRow, css.totalRow)}>
            <dt>Order Total:</dt>
            <dd>{toUSD(total)}</dd>
          </dl>
        </div>
      </>
    );
  }

  makeRetail(order) {
    const { orderId, items, shippingAddress = {} } = order;
    const titleLine = `${items.length} ${pluralize('item', items.length)} will be shipped to:`;
    const { testId } = this.context;
    const [ firstItem ] = items;
    const { shippingInfo: { earliestArrivalDate, latestArrivalDate } } = firstItem;
    const formattedAddress = toFormatted(shippingAddress);

    return (
      <div className={css.orderDetails}>
        <div className={css.orderIdLine}><strong data-test-id={testId('orderId')}>Order: {orderId}</strong></div>
        <div data-test-id={testId('titleLine')}>{titleLine}</div>
        <div className={css.addressBlock}><MultiLineAddress address={formattedAddress} highlightName={false} /></div>
        <div className={css.estimatedDeliveryBlock}>Estimated Delivery: {this.makePromise(earliestArrivalDate, latestArrivalDate)}</div>
      </div>
    );
  }

  makePromise(earliestArrivalDate, latestArrivalDate) {
    return earliestArrivalDate === latestArrivalDate
      ? <span className={css.dateRange}>{earliestArrivalDate}</span>
      : <span className={css.dateRange}>{earliestArrivalDate} - {latestArrivalDate}</span>;
  }

  makeEGiftCard(order) {
    const { orderId, items } = order;
    const { emailAddress } = items[0].egcInfo;
    const titleLine = `${items.length} gift ${pluralize('card', items.length)} ${pluralize('is', items.length)} being sent to:`;
    const deliveryMsg = 'Estimated delivery is in 5 minutes, but in rare circumstances delivery may be delayed.';
    const { testId } = this.context;

    return (
      <div className={css.orderDetails}>
        <div className={css.orderIdLine}><strong data-test-id={testId('egcOrderId')}>Order: {orderId}</strong></div>
        <div data-test-id={testId('egcTitleLine')}>{titleLine}</div>
        <div className={css.addressBlock} data-test-id={testId('egcEmailAddress')}>{emailAddress}</div>
        <div className={css.egcDelivery} data-test-id={testId('egcDeliveryMsg')}>{deliveryMsg}</div>
      </div>
    );
  }

  render() {
    const { melodyCheckoutData: { orderInfo } } = this.props;
    const {
      marketplace: {
        orderConfirmation: {
          line1Text,
          line2Text,
          line3Text,
          showContactUs,
          showVoc,
          showActionButtons,
          is2ColLayout
        }
      }
    } = this.context;

    if (!orderInfo) {
      return <Loader />;
    }

    const { testId } = this.context;
    const reviewLink = orderInfo.length > 1 ? '/account' : `/orders/${orderInfo[0].orderId}`;
    const reviewLinkText = orderInfo.length > 1 ? 'Review or edit your recent orders' : 'Review or edit your order';

    const headerText = (
      <>
        <h1>{line1Text}</h1>
        { !!line2Text && <p className={css.line2Text}>{line2Text}</p>}
        { !!line3Text && <p>{line3Text}</p>}
      </>
    );

    return (
      <SiteAwareMetadata>
        <div className={css.page}>
          <div className={css.smallScreenOnly}>
            {headerText}
          </div>

          <div className={css.container}>
            <div className={cn(css.desktopOnly, css.orderPlaced)}>
              {headerText}
            </div>

            {
              orderInfo.map((order, i) => (
                <div key={`order-${i}`} data-test-id={testId('orderBlock')}>
                  { is2ColLayout ? this.make2ColOrder(order) : this.makeOrder(order) }
                </div>
              ))
            }

            {
              showActionButtons && <div className={css.actions}>
                <Link
                  className={css.reviewOrderBtn}
                  data-test-id={testId('orderReviewLink')}
                  to={reviewLink}>{reviewLinkText}</Link>

                <div>or</div>

                <Link
                  className={css.continueBtn}
                  data-test-id={testId('continueShoppingTop')}
                  to="/?referrer=thankyou"
                  onClick={this.onClickContinueShopping}>Continue Shopping</Link>

              </div>
            }
          </div>

          <div>
            { showContactUs && <ContactUs /> }
            { showVoc && <VoiceOfCustomer /> }
          </div>
        </div>
      </SiteAwareMetadata>
    );
  }
}

const mapStateToProps = ({ melodyCheckoutData }) => ({ melodyCheckoutData });

OrderConfirmation.contextTypes = { testId: PropTypes.func, marketplace: PropTypes.object };

export default connect(mapStateToProps, {
  firePixelServer,
  onFetchOrdersByPurchaseId,
  setHeaderFooterVisibility,
  setLastPageWasOrderconfirmation
})(OrderConfirmation);
